import { ReleaseNoteSectionViewOf } from 'bff';
import { ReleaseNoteComparisonTone } from 'database';
import { cn } from 'ui';
import { InlineText } from '../RichText';

// The accent column is the one the note is about: shaded, in full ink.
const toneClassNames: Record<
  ReleaseNoteComparisonTone,
  { cell: string; label: string }
> = {
  accent: { cell: 'bg-primary/5 text-foreground', label: 'text-primary' },
  neutral: { cell: 'text-muted-foreground', label: 'text-muted-foreground' },
};

// A table once the columns fit side by side; on a phone every row becomes a
// block with each cell under its column's label.
export const ComparisonSection = ({
  section: { content },
}: {
  section: ReleaseNoteSectionViewOf<'comparison'>;
}) => (
  <div className='overflow-hidden rounded border border-stone-200 bg-white'>
    <table className='hidden w-full border-collapse text-sm md:table'>
      <thead>
        <tr className='border-b border-stone-200'>
          <th scope='col' className='w-44 px-4 py-3' />
          {content.columns.map((column) => (
            <th
              key={column.label}
              scope='col'
              className={cn(
                'px-4 py-3 text-left text-[0.8125rem] font-bold',
                toneClassNames[column.tone].cell,
                toneClassNames[column.tone].label,
              )}
            >
              {column.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody className='divide-y divide-stone-100'>
        {content.rows.map((row) => (
          <tr key={row.label} className='align-top'>
            <th
              scope='row'
              className='px-4 py-3 text-left font-semibold text-foreground'
            >
              {row.label}
            </th>
            {row.cells.map((cell, index) => (
              <td
                key={index}
                className={cn(
                  'px-4 py-3 leading-relaxed',
                  toneClassNames[content.columns[index].tone].cell,
                )}
              >
                <InlineText text={cell} />
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>

    <dl className='divide-y divide-stone-100 md:hidden'>
      {content.rows.map((row) => (
        <div key={row.label} className='flex flex-col gap-3 p-4'>
          <dt className='font-semibold text-foreground'>{row.label}</dt>
          {row.cells.map((cell, index) => {
            const { tone, label } = content.columns[index];

            return (
              <dd
                key={index}
                className={cn(
                  'flex flex-col gap-1 border-l-[3px] pl-3',
                  tone === 'accent' ? 'border-primary' : 'border-stone-200',
                )}
              >
                <span
                  className={cn(
                    'text-xs font-bold',
                    toneClassNames[tone].label,
                  )}
                >
                  {label}
                </span>
                <p
                  className={cn(
                    'text-sm leading-relaxed',
                    tone === 'accent'
                      ? 'text-foreground'
                      : 'text-muted-foreground',
                  )}
                >
                  <InlineText text={cell} />
                </p>
              </dd>
            );
          })}
        </div>
      ))}
    </dl>
  </div>
);
